import React, { useEffect } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import Modal from 'components/Modal';
import DatePickerComponent from 'components/Datepicker/DatePickerComponent';
import H5 from 'components/H5';
import { Colors } from 'modules/theme';
import Icon from 'components/Icon';
import ButtonText from 'components/ButtonText';
import PropTypes from 'prop-types';
import {
  buildRangeSummary,
  getIntervalGroups,
  getIntervalLabel,
  getQuickIntervalValues,
  HOURLY_ROLLUP_RETENTION_DAYS,
  isBeforeHourlyRetention,
  isConsumptionRangeOverTwoMonths,
} from 'constants/consumptionIntervals';

const INPUT_RADIUS = '8px';

const Shell = styled.div`
  max-width: 520px;
  margin: 0 auto;
`;

const Section = styled.section`
  background: #f8fbff;
  border: 1px solid #e8f1f8;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 14px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
`;

const SectionTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${Colors.blue};
`;

const PresetRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
`;

const PresetButton = styled.button`
  border: 1px solid ${props => (props.active ? Colors.lightBlue : '#d7e6f2')};
  background: ${props => (props.active ? '#e8f4fd' : '#fff')};
  color: ${props => (props.active ? Colors.lightBlue : '#4a5568')};
  border-radius: 999px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${Colors.lightBlue};
    color: ${Colors.lightBlue};
    background: #eef7fd;
  }
`;

const DateGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;

  @media (min-width: 520px) {
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
`;

const DateField = styled.div`
  min-width: 0;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: ${Colors.lightBlue};
  margin-bottom: 6px;
  letter-spacing: 0.01em;
`;

const DateInputWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .react-datepicker-wrapper {
    flex: 1;
    min-width: 0;
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

const IntervalChip = styled.button`
  border: 1px solid ${props => (props.active ? Colors.lightBlue : '#d7e6f2')};
  background: ${props => (props.active ? Colors.lightBlue : '#fff')};
  color: ${props => (props.active ? '#fff' : '#4a5568')};
  border-radius: ${INPUT_RADIUS};
  min-width: 54px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: ${props => (props.active ? '0 2px 8px rgba(20, 152, 203, 0.25)' : 'none')};

  &:hover {
    border-color: ${Colors.lightBlue};
    color: ${props => (props.active ? '#fff' : Colors.lightBlue)};
  }
`;

const SelectWrapper = styled.div`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 14px;
    width: 0;
    height: 0;
    pointer-events: none;
    transform: translateY(-25%);
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid ${Colors.lightBlue};
  }
`;

const IntervalSelect = styled.select`
  width: 100%;
  height: 44px;
  padding: 10px 36px 10px 12px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  border: 1px solid #bebcbc;
  border-radius: ${INPUT_RADIUS};
  background-color: #fff;
  outline: none;
  box-sizing: border-box;
  cursor: pointer;
  transition: border-color 0.15s ease;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  &:hover {
    border-color: #999;
  }

  &:focus {
    border-color: #1498cb;
    box-shadow: 0 0 0 3px rgba(20, 152, 203, 0.12);
  }
`;

const SummaryBar = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #eef6fc;
  border: 1px solid #d6ebf7;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 18px;
`;

const SummaryText = styled(H5)`
  margin: 0;
  color: #3d4f5f;
  font-size: 13px;
  line-height: 1.45;
`;

const Footer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 4px;

  button {
    border-radius: ${INPUT_RADIUS} !important;
    min-width: 120px;
  }
`;

const SimpleForm = styled.div`
  max-width: 440px;
  margin: 0 auto;
`;

const SimpleDateGrid = styled(DateGrid)`
  margin-bottom: 18px;
`;

const renderIntervalOptions = intervalOptions =>
  intervalOptions.some(group => group.options)
    ? intervalOptions.map(group => (
        <optgroup key={group.label} label={group.label}>
          {group.options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </optgroup>
      ))
    : intervalOptions.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ));

/**
 * Both long presets stay inside HOURLY_ROLLUP_RETENTION_DAYS so daily grouping still
 * has hourly rollups to read. `subtract(6, 'months')` would land 181-184 days back
 * depending on the month, past the TTL, so the 6-month preset is defined in days.
 */
const SIX_MONTHS_PRESET_DAYS = HOURLY_ROLLUP_RETENTION_DAYS - 1;

const QUICK_RANGES = [
  { id: '24h', label: 'Last 24 hours', hours: 24 },
  { id: '7d', label: 'Last 7 days', days: 7 },
  { id: '30d', label: 'Last 30 days', days: 30 },
  { id: '3mo', label: 'Last 3 months', days: 90 },
  { id: '6mo', label: 'Last 6 months', days: SIX_MONTHS_PRESET_DAYS },
  { id: 'install', label: 'Since installation', useMinDate: true },
];

const getActivePreset = (dateFrom, dateTo, minDate) => {
  const from = moment(dateFrom);
  const to = moment(dateTo);
  const now = moment();

  if (!from.isValid() || !to.isValid()) return null;
  if (!to.isSame(now, 'minute') && !to.isAfter(now.clone().subtract(2, 'minutes'))) return null;

  const hoursDiff = to.diff(from, 'hours', true);
  const daysDiff = to.diff(from, 'days', true);

  if (Math.abs(hoursDiff - 24) < 0.5) return '24h';
  if (Math.abs(daysDiff - 7) < 0.5) return '7d';
  if (Math.abs(daysDiff - 30) < 1) return '30d';
  if (Math.abs(daysDiff - 90) < 1) return '3mo';
  if (Math.abs(daysDiff - SIX_MONTHS_PRESET_DAYS) < 1) return '6mo';
  if (minDate && from.isSame(moment(minDate), 'minute')) return 'install';

  return null;
};

export const ModalDatePicker = ({
  isVisible,
  switchModal,
  dateFrom,
  dateTo,
  handleDateFrom,
  handleDateTo,
  saveChanges,
  minDate,
  interval,
  onIntervalChange,
  intervalOptions,
}) => {
  const hasInterval = intervalOptions && intervalOptions.length > 0;
  const groupedOptions =
    hasInterval && intervalOptions[0]?.options
      ? getIntervalGroups(dateFrom, dateTo, intervalOptions)
      : getIntervalGroups(dateFrom, dateTo);
  const activePreset = getActivePreset(dateFrom, dateTo, minDate);
  const quickIntervals = hasInterval ? getQuickIntervalValues(dateFrom, dateTo) : [];
  const isLongRange = hasInterval && isConsumptionRangeOverTwoMonths(dateFrom, dateTo);
  const showCustomSelect = hasInterval && interval && !quickIntervals.includes(interval);
  const showIntervalSelect = hasInterval && !isLongRange;
  const summary = hasInterval ? buildRangeSummary(dateFrom, dateTo, interval) : null;

  useEffect(() => {
    if (!hasInterval || !onIntervalChange) {
      return;
    }
    // Outside these two cases the user may keep a custom interval via the select.
    if (!isLongRange && !isBeforeHourlyRetention(dateFrom)) {
      return;
    }

    const allowedIntervals = getQuickIntervalValues(dateFrom, dateTo);

    if (interval && !allowedIntervals.includes(interval)) {
      onIntervalChange(allowedIntervals[0] ?? 'auto');
    }
  }, [dateFrom, dateTo, hasInterval, interval, isLongRange, onIntervalChange]);

  const applyPreset = preset => {
    const to = moment().toDate();
    let from;

    if (preset.useMinDate && minDate) {
      from = moment(minDate).toDate();
    } else if (preset.hours) {
      from = moment()
        .subtract(preset.hours, 'hours')
        .toDate();
    } else if (preset.days) {
      from = moment()
        .subtract(preset.days, 'days')
        .toDate();
    }

    if (from) {
      handleDateFrom(from);
    }
    handleDateTo(to);
  };

  if (!hasInterval) {
    return (
      <Modal
        title="Choose a time period"
        onClose={switchModal}
        isVisible={isVisible}
        size="medium"
        height="little"
        zIndex={3500}
      >
        <SimpleForm>
          <SimpleDateGrid>
            <DateField>
              <FieldLabel>From</FieldLabel>
              <DateInputWrap>
                <DatePickerComponent
                  key={`from-${+moment(dateFrom)}`}
                  minDate={minDate}
                  date={dateFrom}
                  onChange={handleDateFrom}
                />
                <Icon
                  name="calendar-alt"
                  color={Colors.lightBlue}
                  customStyle={{ fontSize: '18px' }}
                />
              </DateInputWrap>
            </DateField>
            <DateField>
              <FieldLabel>To</FieldLabel>
              <DateInputWrap>
                <DatePickerComponent
                  key={`to-${+moment(dateTo)}`}
                  minDate={minDate}
                  date={dateTo}
                  onChange={handleDateTo}
                />
                <Icon
                  name="calendar-alt"
                  color={Colors.lightBlue}
                  customStyle={{ fontSize: '18px' }}
                />
              </DateInputWrap>
            </DateField>
          </SimpleDateGrid>
          <Footer>
            <ButtonText onClick={switchModal} text="Cancel" theme="basic" />
            <ButtonText onClick={saveChanges} text="Save" theme="primary" />
          </Footer>
        </SimpleForm>
      </Modal>
    );
  }

  return (
    <Modal
      title="Choose a time period"
      onClose={switchModal}
      isVisible={isVisible}
      size="medium"
      height="little"
      zIndex={3500}
    >
      <Shell>
        <PresetRow>
          {QUICK_RANGES.map(preset => (
            <PresetButton
              key={preset.id}
              type="button"
              active={activePreset === preset.id}
              onClick={() => applyPreset(preset)}
            >
              {preset.label}
            </PresetButton>
          ))}
        </PresetRow>

        <Section>
          <SectionHeader>
            <Icon name="calendar-alt" color={Colors.lightBlue} customStyle={{ fontSize: '15px' }} />
            <SectionTitle>Date range</SectionTitle>
          </SectionHeader>
          <DateGrid>
            <DateField>
              <FieldLabel>From</FieldLabel>
              <DateInputWrap>
                <DatePickerComponent
                  key={`from-${+moment(dateFrom)}`}
                  minDate={minDate}
                  date={dateFrom}
                  onChange={handleDateFrom}
                />
              </DateInputWrap>
            </DateField>
            <DateField>
              <FieldLabel>To</FieldLabel>
              <DateInputWrap>
                <DatePickerComponent
                  key={`to-${+moment(dateTo)}`}
                  minDate={minDate}
                  date={dateTo}
                  onChange={handleDateTo}
                />
              </DateInputWrap>
            </DateField>
          </DateGrid>
        </Section>

        <Section>
          <SectionHeader>
            <Icon name="chart-bar" color={Colors.lightBlue} customStyle={{ fontSize: '15px' }} />
            <SectionTitle>Chart grouping</SectionTitle>
          </SectionHeader>
          <ChipRow>
            {quickIntervals.map(value => (
              <IntervalChip
                key={value}
                type="button"
                active={interval === value}
                onClick={() => onIntervalChange(value)}
              >
                {getIntervalLabel(value, true)}
              </IntervalChip>
            ))}
          </ChipRow>
          {showIntervalSelect && (
            <SelectWrapper>
              <IntervalSelect
                value={showCustomSelect ? interval : ''}
                onChange={event => onIntervalChange(event.target.value)}
              >
                <option value="" disabled hidden>
                  More intervals…
                </option>
                {renderIntervalOptions(groupedOptions)}
              </IntervalSelect>
            </SelectWrapper>
          )}
        </Section>

        {summary && (
          <SummaryBar>
            <Icon
              name="info-circle"
              color={Colors.lightBlue}
              customStyle={{ fontSize: '16px', marginTop: 2 }}
            />
            <SummaryText>{summary.text}</SummaryText>
          </SummaryBar>
        )}

        <Footer>
          <ButtonText onClick={switchModal} text="Cancel" theme="basic" />
          <ButtonText onClick={saveChanges} text="Apply" theme="primary" />
        </Footer>
      </Shell>
    </Modal>
  );
};

ModalDatePicker.propTypes = {
  dateFrom: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  dateTo: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  handleDateFrom: PropTypes.func,
  handleDateTo: PropTypes.func,
  interval: PropTypes.string,
  intervalOptions: PropTypes.array,
  isVisible: PropTypes.bool,
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onIntervalChange: PropTypes.func,
  saveChanges: PropTypes.func,
  switchModal: PropTypes.func,
};
